<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useArrowsAiAssistants } from 'dashboard/composables/useArrowsAiAssistants';
import { useUnsavedChangesGuard } from 'dashboard/composables/useUnsavedChangesGuard';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsPageLayout from 'dashboard/components-next/arrowsAi/settings/SettingsPageLayout.vue';
import AssistantForm from 'dashboard/components-next/arrowsAi/assistant/AssistantForm.vue';
import MissingAssistant from 'dashboard/components-next/arrowsAi/MissingAssistant.vue';
import UnsavedChangesDialog from 'dashboard/components-next/arrowsAi/UnsavedChangesDialog.vue';
import DeleteAssistantDialog from 'dashboard/components-next/arrowsAi/assistant/DeleteAssistantDialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { getAssistant, rememberAssistant, updateAssistant, deleteAssistant } =
  useArrowsAiAssistants();
const formRef = ref(null);
const formDirty = ref(false);
const deleteDialogRef = ref(null);
const isDeleting = ref(false);
const isDirty = computed(() => formDirty.value);

const assistant = computed(() => {
  const current = getAssistant(route.params.assistantId);
  if (current) rememberAssistant(current.id);
  return current;
});

const persistSettings = async () => {
  const payload = formRef.value?.getPayload();
  if (!assistant.value || !payload) {
    useAlert(t('ARROWS_AI.SETTINGS.ERROR'));
    throw new Error('missing-assistant');
  }
  if (!payload.name.trim() || !payload.description.trim()) {
    useAlert(t('ARROWS_AI.ASSISTANTS.CREATE.ERROR'));
    throw new Error('invalid');
  }

  try {
    await updateAssistant(assistant.value.id, payload);
    useAlert(t('ARROWS_AI.SETTINGS.SUCCESS', { name: payload.name.trim() }));
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
    throw error;
  }
};

const handleSave = () => persistSettings().catch(() => {});

const { dialogRef, confirmSave, discard, stay } = useUnsavedChangesGuard({
  isDirty,
  save: persistSettings,
});

const handleDelete = () => {
  if (!assistant.value || isDeleting.value) return;
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  if (!assistant.value || isDeleting.value) return;

  isDeleting.value = true;
  try {
    const nextId = await deleteAssistant(assistant.value.id);
    deleteDialogRef.value?.close();
    useAlert(t('ARROWS_AI.SETTINGS.DELETED'));
    if (nextId) {
      router.push({
        name: 'arrows_ai_assistants_overview',
        params: { accountId: route.params.accountId, assistantId: nextId },
      });
      return;
    }
    router.push({
      name: 'arrows_ai_assistants_index',
      params: { accountId: route.params.accountId },
    });
  } catch (error) {
    useAlert(error.message || t('ARROWS_AI.SETTINGS.ERROR'));
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <SettingsPageLayout
    :heading="t('ARROWS_AI.SETTINGS.BASIC.TITLE')"
    :description="t('ARROWS_AI.SETTINGS.BASIC.DESCRIPTION')"
  >
    <MissingAssistant v-if="!assistant" />
    <div v-else class="flex flex-col gap-8">
      <AssistantForm
        ref="formRef"
        :assistant="assistant"
        :submit-label="t('ARROWS_AI.SAVE')"
        :show-cancel="false"
        show-schedule
        @submit="handleSave"
        @dirty="formDirty = $event"
      />
      <span class="w-full h-px bg-n-weak" />
      <div class="flex items-end justify-between gap-4">
        <div>
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('ARROWS_AI.SETTINGS.DELETE.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('ARROWS_AI.SETTINGS.DELETE.DESCRIPTION') }}
          </p>
        </div>
        <Button
          color="ruby"
          :label="
            t('ARROWS_AI.SETTINGS.DELETE.BUTTON', {
              assistantName: assistant.name,
            })
          "
          @click="handleDelete"
        />
      </div>
    </div>
  </SettingsPageLayout>
  <DeleteAssistantDialog
    v-if="assistant"
    ref="deleteDialogRef"
    :assistant-name="assistant.name"
    :is-loading="isDeleting"
    @confirm="confirmDelete"
  />
  <UnsavedChangesDialog
    ref="dialogRef"
    @save="confirmSave"
    @discard="discard"
    @stay="stay"
  />
</template>
